import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Users, Handshake, Globe, BarChart3, Target, Building2,
  MapPin, Star, Briefcase, Phone, Mail, ArrowLeft,
  QrCode, Printer, TrendingUp
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const Brochure = () => {
  const [qrCodes, setQrCodes] = useState({});

  useEffect(() => {
    const fetchQR = async () => {
      try {
        const response = await axios.get(`${API}/qr/all-base64?color=black`);
        const map = {};
        response.data.qr_codes.forEach((qr) => { map[qr.id] = qr; });
        setQrCodes(map);
      } catch (e) {
        // QR codes are optional — brochure renders without them
        setQrCodes({});
      }
    };
    fetchQR();
  }, []);

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-white">
      {/* Print Controls */}
      <div className="print:hidden bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" alt="iConcierge Logo" className="h-10 w-auto" />
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" asChild><Link to="/qr-codes"><QrCode className="w-4 h-4 mr-2" />QR Codes</Link></Button>
              <Button variant="ghost" asChild><Link to="/"><ArrowLeft className="w-4 h-4 mr-2" />Home</Link></Button>
              <Button onClick={handlePrint} className="bg-[#0284C7] text-white hover:bg-[#0270a8] rounded-full" data-testid="print-brochure-btn">
                <Printer className="w-4 h-4 mr-2" />Print / Save as PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== TRI-FOLD BROCHURE ===== */}
      <div className="max-w-[1200px] mx-auto my-8 print:my-0">
        <div className="grid grid-cols-3 min-h-[800px] print:min-h-[100vh] border border-slate-200 print:border-0" data-testid="brochure-trifold">

          {/* ===== LEFT PANEL: WHO WE ARE ===== */}
          <div className="bg-white p-8 flex flex-col relative overflow-hidden border-r border-slate-100">
            {/* Subtle bg curve */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#E8F4FD] rounded-full -translate-y-1/2 translate-x-1/3 opacity-50"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#E8F4FD] rounded-full translate-y-1/3 -translate-x-1/3 opacity-40"></div>

            <div className="relative z-10 flex flex-col h-full">
              {/* Logo */}
              <div className="mb-8">
                <img src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" alt="iConcierge Logo" className="h-16 w-auto mb-1" />
              </div>

              {/* Who We Are */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-[#123F70] mb-1">
                  WHO <span className="text-[#0284C7]">WE ARE</span>
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  ICONCIERGEHM is a boutique healthcare marketing firm dedicated to building meaningful relationships that drive growth, referrals, and long-term impact for healthcare organizations.
                </p>
              </div>

              {/* Services */}
              <div className="space-y-5 flex-1">
                {[
                  { icon: Users, title: "Healthcare Growth Strategy", desc: "Customized strategies that align with your mission and market." },
                  { icon: Handshake, title: "Provider Referral Development", desc: "Build stronger connections that drive consistent patient referrals." },
                  { icon: Globe, title: "Market Expansion Consulting", desc: "Identify opportunities and expand your reach with confidence." },
                  { icon: TrendingUp, title: "Business Solutions", desc: "Practical solutions that fuel sustainable growth and measurable results." },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3">
                    <div className="w-9 h-9 bg-[#E8F4FD] rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4.5 h-4.5 text-[#0284C7]" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#123F70]">{item.title}</h4>
                      <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer tagline */}
              <div className="mt-6 pt-4">
                <p className="text-xs font-semibold text-[#0284C7] tracking-wider uppercase">Where Healthcare Relationships</p>
                <p className="text-2xl text-[#123F70] mt-1" style={{ fontFamily: "'Georgia', serif", fontStyle: "italic" }}>Drive Results</p>
              </div>
            </div>
          </div>

          {/* ===== CENTER PANEL: WHAT WE DELIVER ===== */}
          <div className="bg-white p-8 flex flex-col border-r border-slate-100">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-[#123F70] mb-1">
                WHAT WE <span className="text-[#0284C7]">DELIVER</span>
              </h2>
              <div className="w-10 h-0.5 bg-[#0284C7] mb-3"></div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Comprehensive solutions to elevate your brand, expand your network, and grow your impact.
              </p>
            </div>

            <div className="space-y-5 flex-1">
              {[
                { icon: Target, title: "STRATEGIC HEALTHCARE MARKETING", desc: "Strategic marketing solutions that position your organization for success." },
                { icon: Users, title: "REFERRAL GROWTH STRATEGIES", desc: "Data-driven strategies that increase referrals and strengthen partnerships." },
                { icon: Building2, title: "HOSPITAL / PHYSICIAN RELATIONSHIP DEVELOPMENT", desc: "Cultivating high-value relationships that generate trust and results." },
                { icon: MapPin, title: "MARKET ACCESS & EXPANSION", desc: "Navigate new markets and access opportunities with ease." },
                { icon: Star, title: "BRAND POSITIONING", desc: "Elevate your brand and stand out in a competitive healthcare landscape." },
                { icon: Briefcase, title: "CONSULTING SOLUTIONS", desc: "Expert guidance and hands-on support to accelerate your growth." },
              ].map((item) => (
                <div key={item.title} className="flex gap-3">
                  <div className="w-9 h-9 bg-[#E8F4FD] rounded-full flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4.5 h-4.5 text-[#0284C7]" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-[#123F70] uppercase tracking-wide">{item.title}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ===== RIGHT PANEL: COVER / CTA ===== */}
          <div className="bg-gradient-to-b from-[#E8F4FD] to-white p-8 flex flex-col relative overflow-hidden">
            {/* Decorative curves */}
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#D0EBFA] rounded-full -translate-y-1/2 translate-x-1/3 opacity-60"></div>
            <div className="absolute top-20 left-0 w-40 h-40 bg-[#D0EBFA] rounded-full -translate-x-1/2 opacity-30"></div>

            <div className="relative z-10 flex flex-col h-full">
              {/* Logo */}
              <div className="flex items-center gap-2 mb-6">
                <img src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" alt="Logo" className="h-10 w-auto" />
              </div>

              {/* Headline */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-[#123F70] leading-tight">
                  ELEVATING<br />
                  <span className="text-3xl text-[#0284C7]">HEALTHCARE GROWTH</span><br />
                  THROUGH STRATEGIC<br />
                  MARKETING
                </h1>
                <div className="w-12 h-0.5 bg-[#0284C7] mt-3"></div>
              </div>

              {/* Healthcare images */}
              <div className="grid grid-cols-3 gap-2 mb-6 rounded-xl overflow-hidden">
                <img src="https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200" alt="Partnership" className="w-full h-24 object-cover rounded-lg" />
                <img src="https://images.unsplash.com/photo-1659353888477-6e6aab941b55?crop=entropy&cs=srgb&fm=jpg&q=85&w=200&h=200&fit=crop" alt="Healthcare" className="w-full h-24 object-cover rounded-lg" />
                <img src="https://images.pexels.com/photos/5407222/pexels-photo-5407222.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200" alt="Doctor" className="w-full h-24 object-cover rounded-lg" />
              </div>

              {/* Let's Grow Together + QR codes */}
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-1">
                  <p className="text-2xl text-[#123F70]" style={{ fontFamily: "'Georgia', serif", fontStyle: "italic" }}>Let's</p>
                  <p className="text-xl font-bold text-[#123F70] uppercase">Grow</p>
                  <p className="text-xl font-bold text-[#123F70] uppercase">Together</p>
                </div>
                <div className="flex gap-3">
                  {qrCodes.register && (
                    <div className="text-center">
                      <img src={qrCodes.register.qr_base64} alt="QR Join" className="w-20 h-20 border border-slate-200 rounded-lg" />
                      <p className="text-[9px] text-slate-500 mt-1 font-medium">Scan to Join</p>
                    </div>
                  )}
                  {qrCodes.pricing && (
                    <div className="text-center">
                      <img src={qrCodes.pricing.qr_base64} alt="QR Pricing" className="w-20 h-20 border border-slate-200 rounded-lg" />
                      <p className="text-[9px] text-slate-500 mt-1 font-medium">Scan for Pricing</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 text-sm flex-1">
                <a href="https://iconciergehm.com" className="flex items-center gap-2 text-slate-700">
                  <Globe className="w-4 h-4 text-[#0284C7]" />iconciergehm.com
                </a>
                <a href="tel:8882509421" className="flex items-center gap-2 text-slate-700">
                  <Phone className="w-4 h-4 text-[#0284C7]" />888.250.9421
                </a>
                <a href="mailto:contact@iconciergehm.com" className="flex items-center gap-2 text-slate-700">
                  <Mail className="w-4 h-4 text-[#0284C7]" />contact@iconciergehm.com
                </a>
                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin className="w-4 h-4 text-[#0284C7]" />Serving Healthcare Organizations Nationwide
                </div>
              </div>

              {/* Consultation QR */}
              <div className="flex items-center gap-3 mt-4 pt-3 border-t border-slate-200">
                {qrCodes.browse && (
                  <img src={qrCodes.browse.qr_base64} alt="QR Consultation" className="w-16 h-16 border border-slate-200 rounded-lg" />
                )}
                <div>
                  <p className="text-xs font-bold text-[#123F70] uppercase tracking-wide">Scan to Schedule</p>
                  <p className="text-xs font-bold text-[#123F70] uppercase tracking-wide">a Consultation</p>
                </div>
              </div>

              {/* Bottom tagline */}
              <div className="mt-4 pt-3 border-t border-[#0284C7]/30">
                <p className="text-xs font-bold text-[#0284C7] tracking-widest uppercase text-center">
                  Results-Driven. Relationship-Focused.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:border-0 { border: none !important; }
          .print\\:my-0 { margin: 0 !important; }
          .print\\:min-h-\\[100vh\\] { min-height: 100vh; }
          @page { margin: 0; size: landscape letter; }
        }
      `}</style>
    </div>
  );
};

export default Brochure;
